import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import toast from "react-hot-toast";
import accountsFlowApi from "../../services/accountsFlowService";
import "./OrderCredit.css";

/*
 * Credit hold on a customer-app order (backend accounts_flow/orderApproval.js):
 * an order over the available credit is booked with creditStatus CREDIT_HOLD and
 * needs an approver before Loading Advice. Chip + Approve / Reject for approvers.
 */

export const CREDIT_HOLD = "CREDIT_HOLD";
const LABEL = { CREDIT_HOLD: "Credit hold", APPROVED: "Credit approved", REJECTED: "Credit rejected" };
const TONE = { CREDIT_HOLD: "warn", APPROVED: "ok", REJECTED: "bad" };
const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

let metaPromise = null;
/* One /meta request per page load: can this login approve held orders (and its own)? */
export function useOrderApprover() {
  const [meta, setMeta] = useState({});
  useEffect(() => {
    let alive = true;
    if (!metaPromise) metaPromise = accountsFlowApi.meta().then((r) => r?.data || {}).catch(() => { metaPromise = null; return {}; });
    metaPromise.then((data) => alive && setMeta(data));
    return () => { alive = false; };
  }, []);
  return meta;
}

export function isCreditHeld(order) {
  return order?.creditStatus === CREDIT_HOLD || order?.creditStatus === "REJECTED";
}

function CreditDecisionModal({ show, mode, order, onHide, onDone }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (show) { setText(""); setError(""); } }, [show]);
  const approve = mode === "approve";
  const check = order?.creditCheck || {};

  const submit = async () => {
    if (!text.trim()) return setError(approve ? "Approval note is required" : "Reject reason is required");
    setSaving(true);
    try {
      const orderNo = order.orderSchedulingNr || order.orderNo;
      const result = approve ? await accountsFlowApi.approveOrderCredit(orderNo, text.trim()) : await accountsFlowApi.rejectOrderCredit(orderNo, text.trim());
      toast.success(result?.message || (approve ? "Approved" : "Rejected"));
      onDone && onDone(result?.data);
      onHide();
    } catch (err) {
      setError(err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{approve ? "Approve order on credit hold" : "Reject order"} · {order?.orderSchedulingNr || order?.orderNo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">{order?.customerName}{order?.customerCode ? ` · ${order.customerCode}` : ""}</p>
        {check.creditLimit !== undefined && (
          <ul className="small mb-3 ps-3">
            <li>Order value {inr(check.orderValue)}, available credit when booked {inr(check.available)}</li>
            <li>Over by <strong>{inr(check.exceededBy)}</strong> (limit {inr(check.creditLimit)} − outstanding {inr(check.outstanding)} − pending orders {inr(check.pendingOrders)})</li>
          </ul>
        )}
        <p className="small text-muted mb-2">
          {approve
            ? "Approving lets Loading Advice be created for this order."
            : "Rejecting cancels the order and takes its value out of the customer's pending orders."}
        </p>
        <label className="form-label fw-semibold">{approve ? "Approval note *" : "Reject reason *"}</label>
        <textarea className={`form-control${error ? " is-invalid" : ""}`} rows={3} maxLength={500} value={text} onChange={(e) => { setText(e.target.value); setError(""); }} />
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" onClick={onHide} disabled={saving}>Close</button>
        <button type="button" className={`btn ${approve ? "btn-success" : "btn-danger"}`} onClick={submit} disabled={saving}>{saving ? "Saving…" : approve ? "Approve" : "Reject order"}</button>
      </Modal.Footer>
    </Modal>
  );
}

/* Chip for the order's credit status, with Approve / Reject for approvers while it is on hold. */
export function OrderCreditControl({ order, onDone, compact = false }) {
  const approver = useOrderApprover();
  const [mode, setMode] = useState(null);
  const status = order?.creditStatus;
  if (!status) return null;
  const held = status === CREDIT_HOLD;
  const canApprove = Boolean(approver.canApproveOrders);
  // Only ADMIN / SALES-ADMIN may decide an order they booked themselves (server checks too).
  const ownOrder = Boolean(order.createdBy && approver.userId && String(order.createdBy).toLowerCase() === String(approver.userId).toLowerCase());
  const blockedOwn = ownOrder && !approver.canApproveOwnOrders;
  const ownTip = "You booked this order: another approver (ADMIN or SALES-ADMIN) must approve it";
  return (
    <div className={`flow-credit-control${compact ? " compact" : ""}`}>
      <span className={`accounts-chip ${TONE[status] || "muted"}`} title={held ? "Over the credit limit: needs approval before Loading Advice" : ""}>{LABEL[status] || status}</span>
      {held && canApprove && (
        <span className="flow-credit-actions">
          <button type="button" className="btn btn-sm btn-success" disabled={blockedOwn} title={blockedOwn ? ownTip : ""} onClick={() => setMode("approve")}>Approve</button>
          <button type="button" className="btn btn-sm btn-outline-danger" disabled={blockedOwn} title={blockedOwn ? ownTip : ""} onClick={() => setMode("reject")}>Reject</button>
        </span>
      )}
      {held && !canApprove && !compact && <span className="small text-muted">Waiting for approval</span>}
      <CreditDecisionModal show={Boolean(mode)} mode={mode} order={order} onHide={() => setMode(null)} onDone={onDone} />
    </div>
  );
}
